import React from "react";
import {observer} from "mobx-react-lite";
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import NoticeItem from "../../../stores/domainStore/pages/models/noticeItem";
import ModalDetailContent from "../../molecules/modalDetailContent/modalDetailContent";
import TimeAndDateFormat from "../../atoms/timeAndDateFormat/timeAndDateFormat";
import {useTranslation} from "react-i18next";
import "./noticeDetailModal.sass";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {NoticeStatus} from "../../../shared/enums/noticeStatus";
import {IconSource} from "../../../shared/enums/iconSource";
import NavigationButton from "../../atoms/button/navigationButton";
import Icon from "../../atoms/common/icon/icon";
import Tag from "../../atoms/tag/tag";
import Banner from "../../atoms/banner/banner";
import {MultiVillageHelper} from "../../../shared/multiVillageHelper";
import {markdownToPlainTextConverter} from "../../../utils/markdownConverter";
import {useStores} from "../../../stores/useStores";

/**
 * The interface for Notice Detail Modal.
 */
export interface INoticeDetailModalProps {
  /**
   * The notice detail to be displayed on the modal.
   */
  noticeDetail: NoticeItem | null;
  /**
   * Whether the modal is loading notice detail or not. When set to true, skeleton text will be displayed.
   */
  isLoading: boolean;
  /**
   * Whether the modal is opened or not. When set to true, the modal will be shown.
   */
  isOpen: boolean;
  /**
   * The handler to handle the toggling event of the modal.
   */
  toggle: () => void;
  /**
   * The handler to handle the edit button clicked event.
   */
  onEditClick: () => void;

  onViewClick: () => void;
}

/**
 * The modal that contains the detail of a notice.
 */
const NoticeDetailModal: React.FC<INoticeDetailModalProps> = ({
  noticeDetail,
  isLoading,
  isOpen,
  toggle,
  onEditClick,
  onViewClick
}) => {
  const {
    uiStore: {accountStore}
  } = useStores();

  const {t} = useTranslation(["translation", "notice"]);

  const isStatusValid =
    noticeDetail?.status === NoticeStatus.Draft ||
    noticeDetail?.status === NoticeStatus.Published ||
    noticeDetail?.status === NoticeStatus.Scheduled;

  const isMultiVillage = !!noticeDetail!.isMultiVillage;

  let canEditNotice =
    isStatusValid &&
    (accountStore.abilities.can("Update", "MultiVillage") || !isMultiVillage);
  if (canEditNotice && noticeDetail?.isAnnouncement) {
    if (!accountStore.abilities.can("Update", "Announcement")) {
      canEditNotice = false;
    }
  }

  // TODO: get village label list for notice
  const villageList = noticeDetail!.villages.selectedItems.map((i) => i.label);

  function viewOrEditButton(canEditNotice: boolean) {
    return (
      <div className="edit-button">
        {canEditNotice ? (
          <ButtonIcon
            type="primary"
            icon={IconSource.edit}
            onClick={onEditClick}
          >
            {t("translation:actions.edit")}
          </ButtonIcon>
        ) : (
          <ButtonIcon
            type="primary"
            icon={IconSource.show}
            onClick={onViewClick}
          >
            {t("translation:actions.view")}
          </ButtonIcon>
        )}
      </div>
    );
  }

  const getTagColor = (status: NoticeStatus) => {
    switch (status) {
      case NoticeStatus.Published:
        return "success";
      case NoticeStatus.Scheduled:
        return "warning";
      case NoticeStatus.Draft:
        return "system";
      case NoticeStatus.Expired:
      default:
        return "neutral";
    }
  };

  return (
    noticeDetail && (
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        modalClassName="notice-detail-modal"
        centered
        data-testid="noticeDetailModal"
      >
        <ModalHeader
          toggle={toggle}
          close={<NavigationButton icon={IconSource.exit} onClick={toggle} />}
        >
          <div>
            {noticeDetail.notificationDate &&
            noticeDetail.status !== NoticeStatus.Draft ? (
              <Icon source={IconSource.notification} />
            ) : null}
            <div>
              {isLoading
                ? t("translation:loading")
                : noticeDetail.title.text.trim() !== ""
                ? noticeDetail.title.text
                : t("notice:titles.untitledNotice")}
            </div>
          </div>
        </ModalHeader>
        {noticeDetail.isMultiVillage &&
          !accountStore.abilities.can("Update", "MultiVillage") && (
            <Banner
              text={t("notice:messages.multiVillageNoticeBanner", {
                creator: noticeDetail.creatorName
              })}
              icon={IconSource.info}
            />
          )}
        <ModalBody>
          <div className="detail-title">
            {t("notice:titles.details")}
            {!isLoading && noticeDetail?.status && (
              <Tag
                text={noticeDetail.status}
                type={getTagColor(noticeDetail.status)}
              />
            )}
          </div>
          {noticeDetail.isMultiVillage && (
            <ModalDetailContent
              icon={IconSource.village}
              isLoading={isLoading}
              content={
                MultiVillageHelper.VillageEllipsis(
                  t,
                  villageList,
                  accountStore.abilities.can("Update", "MultiVillage")
                ) || "-"
              }
              dataTestId="noticeDetailModal-villages"
            />
          )}
          <ModalDetailContent
            icon={IconSource.residents}
            isLoading={isLoading}
            content={noticeDetail.author.text || "-"}
          />
          {noticeDetail.status !== NoticeStatus.Draft && (
            <ModalDetailContent
              icon={IconSource.published}
              isLoading={isLoading}
            >
              <div>
                <TimeAndDateFormat
                  timeAndDate={noticeDetail.publishDate ?? new Date()}
                  timeStyle={"hidden"}
                  dateStyle={"full"}
                />
              </div>
            </ModalDetailContent>
          )}
          {noticeDetail.status !== NoticeStatus.Draft &&
            noticeDetail.expiryDate.date && (
              <ModalDetailContent
                icon={IconSource.expired}
                isLoading={isLoading}
              >
                <div>
                  <TimeAndDateFormat
                    timeAndDate={noticeDetail.expiryDate.date}
                    timeStyle={"hidden"}
                    dateStyle={"full"}
                  />
                </div>
              </ModalDetailContent>
            )}
          <ModalDetailContent
            icon={IconSource.description}
            isCentred={false}
            isLoading={isLoading}
          >
            <div className="notice-body-container">
              {markdownToPlainTextConverter(noticeDetail.body.text) || "-"}
            </div>
          </ModalDetailContent>
          {[
            NoticeStatus.Expired,
            NoticeStatus.Published,
            NoticeStatus.Unpublished
          ].includes(noticeDetail.status) && (
            <ModalDetailContent
              icon={IconSource.show}
              dataTestId={"seenBy"}
              isLoading={isLoading}
              content={t("notice:labels.residentWithCount", {
                count: noticeDetail.seenBy
              })}
            />
          )}
          {!isLoading && viewOrEditButton(canEditNotice)}
        </ModalBody>
      </Modal>
    )
  );
};

export default observer(NoticeDetailModal);
